import React from 'react';
import { Factory, ShoppingBag, Calendar, Wifi, Barcode, CreditCard, Tag, Shield, Scale } from 'lucide-react';
import { FaPrint, FaHandHolding, FaBalanceScale, FaBoxOpen } from "react-icons/fa";

export default function IndustriesWeServe() {
  const industries = [
    {
      title: "Industrial Solutions",
      description: "Comprehensive coding and marking solutions for manufacturing and industrial environments",
      icon: Factory,
      gradient: "from-blue-600 to-purple-600",
      services: [
        {
          name: "Coding & Marking Printers",
          description: "High-performance industrial printers for product identification",
          icon: FaPrint
        },
        {
          name: "Handjet Coding Printing",
          description: "Portable handheld printers for date and batch coding",
          icon: FaHandHolding
        },
        {
          name: "Industrial Weighing Machines",
          description: "Specialized systems for expiration date marking",
          icon: FaBalanceScale
        },
        {
          name: "Packaging Machine",
          description: "Specialized systems for expiration date marking",
          icon: FaBoxOpen
        }
      ]
    },
    {
      title: "Retail Solutions",
      description: "Complete retail technology solutions to enhance customer experience and operations",
      icon: ShoppingBag,
      gradient: "from-emerald-600 to-teal-600",
      services: [
        {
          name: "EAS Antenna Systems",
          description: "Electronic article surveillance for theft prevention",
          icon: Shield
        },
        {
          name: "Electronic Shelf Labels",
          description: "Digital price tags and product information displays",
          icon: Wifi
        },
        {
          name: "Retail Weighing Scales & Ai scales",
          description:"Accurate, efficient, and user-friendly weighing solutions for retail environments",
          icon: Scale
        },
        {
          name: "Barcode Products",
          description: "Complete barcode scanning and printing solutions",
          icon: Barcode
        },
        {
          name: "Tags & Labels",
          description: "Custom tags and labels for product identification",
          icon: Tag
        },
        {
          name: "Point of Sale Systems",
          description: "Modern POS terminals and payment solutions",
          icon: CreditCard
        }
      ]
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-10"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 bg-clip-text text-transparent mb-6">
            Industries We Serve
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-poppins">
            Delivering cutting-edge technology solutions across industrial and retail sectors, 
            empowering businesses with innovative printing, identification, and automation systems.
          </p>
        </div>

        {/* Industries Grid */}
        <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
          {industries.map((industry, index) => (
            <div
              key={index}
              className="group bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 p-8 relative overflow-hidden"
            >
              {/* Card background gradient */}
              <div className={`absolute inset-0 bg-gradient-to-r ${industry.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-3xl`}></div>
              
              {/* Industry header */}
              <div className="relative z-10 mb-8">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${industry.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <industry.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{industry.title}</h3>
                <p className="text-gray-600 text-lg leading-relaxed font-poppins">{industry.description}</p>
              </div>

              {/* Services list */}
              <div className="space-y-4 relative z-10">
                {industry.services.map((service, serviceIndex) => (
                  <div
                    key={serviceIndex}
                    className="flex items-start space-x-4 p-4 rounded-xl hover:bg-gray-50 transition-colors duration-300 group/service"
                  >
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${industry.gradient} flex items-center justify-center flex-shrink-0 group-hover/service:scale-110 transition-transform duration-300`}>
                      <service.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-lg mb-1 group-hover/service:text-blue-600 transition-colors duration-300">
                        {service.name}
                      </h4>
                      <p className="text-gray-600 leading-relaxed font-poppins">{service.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Hover effect border */}
              <div className={`absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-gradient-to-r group-hover:${industry.gradient} transition-all duration-500`}></div>
            </div>
          ))}
        </div>

        {/* Call to action */}
        {/* <div className="text-center mt-16">
          <div className="inline-flex items-center space-x-4 bg-white rounded-full px-8 py-4 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <span className="text-lg font-medium text-gray-700">Ready to transform your business?</span>
            <button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold px-6 py-2 rounded-full transition-all duration-300 transform hover:scale-105">
              Get Started
            </button>
          </div>
        </div> */}
      </div>
    </section>
  );
}